import {
  BankOutlined,
  DashboardOutlined,
  GiftFilled,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  SettingOutlined,
  ShopOutlined,
  SolutionOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Button, Col, Layout, Menu, Row, Space, theme } from "antd";
import { createContext, useEffect, useMemo, useState } from "react";

import Language from "./Language";
import { Link } from "react-router-dom";
import Profile from "./Profile";
import { jwtDecode } from "jwt-decode";
import AuthGroup from "../../../configs/authgroup";
import permissions from "../../../configs/permission"; // นำเข้า permission จากไฟล์ mapping
import { useTranslation } from "react-i18next";

export const MenuContext = createContext();

const getUserData = () => {
  const token = localStorage.getItem("token");
  if (!token) return { permissions: [], isSuperuser: false, groups: [] };

  try {
    const decoded = jwtDecode(token);
    return {
      permissions: decoded?.permission || [],
      isSuperuser: decoded?.is_superuser || false,
      groups: Array.isArray(decoded?.group_id) ? decoded.group_id : [],
    };
  } catch (err) {
    console.error("[getUserData] Invalid JWT: ", err);
    return { permissions: [], isSuperuser: false, groups: [] };
  }
};

// ดึง token จาก localStorage
const getToken = () => localStorage.getItem("token");

const AdminLayout = ({ children }) => {
  const { t, i18n } = useTranslation(["menus"]);
  const { Header, Sider, Content } = Layout;
  // landing หลัง login คือ /home → ให้เมนู Dashboard highlight ตั้งแต่แรก
  const [selectedMenuKeys, setSelectedMenuKeys] = useState(() =>
    window.location.pathname === "/home" ? ["dashboard"] : []
  );
  const [openMenuKeys, setOpenMenuKeys] = useState([]);
  const [collapsed, setCollapsed] = useState(false);
  const {
    token: { colorBgContainer },
  } = theme.useToken();
  const [token, setToken] = useState(getToken());

  useEffect(() => {
    const storedToken = getToken();
    setToken(storedToken);
  }, []);

  // ใช้ token เพื่อดึงข้อมูล user
  const {
    permissions: userPermissions,
    isSuperuser,
    groups: userGroups,
  } = useMemo(() => {
    if (!token) return { permissions: [], isSuperuser: false, groups: [] };
    return getUserData(token);
  }, [token]);

  function getItem(
    label,
    key,
    icon,
    children = [],
    requiredPermissions = [],
    userPermissions,
    isSuperuser,
    superAdminOnly = false
  ) {
    // เมนูนี้ให้เฉพาะ Super Admin
    if (superAdminOnly && !isSuperuser) {
      return null;
    }

    if (!isSuperuser) {
      // ตรวจสอบว่าเมนูหลักมี requiredPermissions หรือไม่
      if (Array.isArray(requiredPermissions) && requiredPermissions.length > 0) {
        const companyDwBypass =
          requiredPermissions.includes(permissions.Company?.VIEW) &&
          userGroups.includes(AuthGroup.DestinationWarehouse);
        const customerCompanySaleBypass =
          requiredPermissions.includes(permissions.Customer?.VIEW) && userGroups.includes(AuthGroup.CompanySale);
        const hasPermission =
          companyDwBypass ||
          customerCompanySaleBypass ||
          requiredPermissions.some((perm) => userPermissions?.includes(perm));
        if (!hasPermission) return null; // ซ่อนเมนูหลักถ้าไม่มีสิทธิ์เลย
      }

      // ตรวจสอบเมนูลูกว่ามีสิทธิ์แสดงหรือไม่
      if (Array.isArray(children) && children.length > 0) {
        children = children.filter(Boolean); // ลบเมนูที่ไม่มีสิทธิ์ออก
        if (children.length === 0) return null; // ซ่อนเมนูหลักถ้าไม่มีเมนูลูกให้แสดง
      }
    }

    return { label, key, icon, children };
  }

  const items = useMemo(
    () =>
      [
        // Dashboard ต้องมี Dashboard.VIEW (2026-08-10) — สอดคล้องกับ route /home ใน router.jsx
        // ไม่มีสิทธิ์ = ไม่เห็นเมนู และเข้าทาง URL ตรง ๆ ก็ถูกพาไปหน้าแรกที่เข้าได้
        getItem(
          <Link to="/home">{t("Menu_Dashboard")}</Link>,
          "dashboard",
          <DashboardOutlined />,
          null,
          [permissions.Dashboard?.VIEW],
          userPermissions,
          isSuperuser
        ),
        getItem(
          <Link to="/company">{t("Menu_Company")}</Link>,
          "company",
          <BankOutlined />,
          null,
          [permissions.Company?.VIEW],
          userPermissions,
          isSuperuser
        ),
        getItem(
          <Link to="/customer">{t("Menu_Customer")}</Link>,
          "customer",
          <UserOutlined />,
          null,
          [permissions.Customer?.VIEW],
          userPermissions,
          isSuperuser
        ),
        getItem(
          <Link to="/sales">{t("Menu_Sales")}</Link>,
          "sales",
          <SolutionOutlined />,
          null,
          [permissions.Sale?.VIEW],
          userPermissions,
          isSuperuser
        ),
        getItem(
          t("Menu_Package.Title_Package"),
          "package",
          <GiftFilled />,
          [
            getItem(
              <Link to="/package/originpackage">{t("Menu_Package.Origin_Package")}</Link>,
              "originpackage",
              null,
              null,
              [permissions.OriginPackage?.VIEW],
              userPermissions,
              isSuperuser
            ),
            getItem(
              <Link to="/package/originpackagetransportation">{t("Menu_Package.Origin_Package_Transportation")}</Link>,
              "originpackagetransportation",
              null,
              null,
              [permissions.OriginPackageTransportation?.VIEW],
              userPermissions,
              isSuperuser
            ),
            getItem(
              <Link to="/package/destinationpackage">{t("Menu_Package.Destination_Package")}</Link>,
              "destinationpackage",
              null,
              null,
              [permissions.DestinationPackage?.VIEW],
              userPermissions,
              isSuperuser
            ),
            getItem(
              <Link to="/package/destinationdelivery">{t("Menu_Package.Destination_Delivery")}</Link>,
              "destinationdelivery",
              null,
              null,
              [permissions.DestinationDelivery?.VIEW],
              userPermissions,
              isSuperuser
            ),
            getItem(
              <Link to="/package/destinationpackagedelivery">{t("Menu_Package.Destination_Package_Delivery")}</Link>,
              "destinationpackagedelivery",
              null,
              null,
              [permissions.DestinationPackageDelivery?.VIEW],
              userPermissions,
              isSuperuser
            ),
          ],
          [
            permissions.OriginPackage?.VIEW,
            permissions.OriginPackageTransportation?.VIEW,
            permissions.DestinationPackage?.VIEW,
            permissions.DestinationDelivery?.VIEW,
            permissions.DestinationPackageDelivery?.VIEW,
          ],
          userPermissions,
          isSuperuser
        ),
        getItem(
          t("Menu_Bill.Title_Bill"),
          "bill",
          <GiftFilled />,
          [
            getItem(
              <Link to="/bill/companybill">{t("Menu_Bill.Company_Bill")}</Link>,
              "companybill",
              null,
              null,
              [permissions.CompanyBill?.VIEW],
              userPermissions,
              isSuperuser
            ),
            getItem(
              <Link to="/bill/customerbill">{t("Menu_Bill.Customer_Bill")}</Link>,
              "customerbill",
              null,
              null,
              [permissions.CustomerBill?.VIEW],
              userPermissions,
              isSuperuser
            ),
            getItem(
              <Link to="/summary/report/K"> {t("Menu_Bill.Summary_Report", { type: "K" })}</Link>,
              "summaryReportTypeK",
              null,
              null,
              [permissions.CustomerBill?.VIEW],
              userPermissions,
              isSuperuser,
              true // Super Admin Only
            ),
            getItem(
              <Link to="/summary/report/M"> {t("Menu_Bill.Summary_Report", { type: "M" })}</Link>,
              "summaryReportTypeM",
              null,
              null,
              [permissions.CustomerBill?.VIEW],
              userPermissions,
              isSuperuser,
              true // Super Admin Only
            ),
          ],
          [permissions.CompanyBill?.VIEW, permissions.CustomerBill?.VIEW],
          userPermissions,
          isSuperuser
        ),
        getItem(
          <Link to="/warehouses">{t("Menu_Warehouse")}</Link>,
          "warehouse",
          <ShopOutlined />,
          null,
          [permissions.Warehouse?.VIEW],
          userPermissions,
          isSuperuser
        ),
        // getItem(<Link to="/7">{t('Menu_Customer')}</Link>, '7', <ShopOutlined />),
        getItem(
          t("Menu_User_Management.Title_User_Management"),
          "sub2",
          <SettingOutlined />,
          [
            getItem(
              <Link to="/usermanagement/user">{t("Menu_User_Management.Users")}</Link>,
              "user",
              null,
              null,
              [permissions.User?.VIEW],
              userPermissions,
              isSuperuser
            ),
            getItem(
              <Link to="/usermanagement/group">{t("Menu_User_Management.Groups")}</Link>,
              "group",
              null,
              null,
              [permissions.Group?.VIEW],
              userPermissions,
              isSuperuser
            ),
          ],
          [permissions.User?.VIEW, permissions.Group?.VIEW],
          i18n.language,
          userPermissions,
          isSuperuser
        ),
      ].filter(Boolean),
    [t, userPermissions, isSuperuser, userGroups]
  ); // ใช้ useMemo เพื่อให้เมนูอัปเดตตาม permission

  return (
    <MenuContext.Provider
      value={{
        setOpenMenuKeys,
        setSelectedMenuKeys,
      }}
    >
      <Layout className="admin-layout-root">
        <Sider
          trigger={null}
          collapsible
          collapsed={collapsed}
          onCollapse={(value) => setCollapsed(value)}
          width={240}
          collapsedWidth={80}
        >
          <div className="demo-logo-vertical"></div>
          <Menu
            className="admin-sider-menu"
            theme="dark"
            mode="inline"
            items={items}
            selectedKeys={selectedMenuKeys}
            openKeys={openMenuKeys}
            onOpenChange={(keys) => {
              setOpenMenuKeys(keys);
            }}
            onClick={({ key }) => {
              setSelectedMenuKeys([key]);
            }}
          />
        </Sider>
        <Layout>
          <Header
            style={{
              padding: 0,
              background: colorBgContainer,
            }}
          >
            <Row>
              <Col span={10}>
                <Button
                  type="text"
                  icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                  onClick={() => setCollapsed(!collapsed)}
                  style={{
                    fontSize: "16px",
                    width: 64,
                    height: 64,
                  }}
                />
              </Col>
              <Col span={4} offset={10} style={{ textAlign: "end" }}>
                <Col>
                  <Space style={{ marginRight: "20px" }}>
                    <div>
                      <Language />
                    </div>
                    <div>
                      <Profile />
                    </div>
                  </Space>
                </Col>
              </Col>
            </Row>
          </Header>
          <Content
            style={{
              margin: "24px 16px",
              padding: 24,
              minHeight: 750,
              background: colorBgContainer,
            }}
          >
            {children}
          </Content>
        </Layout>
      </Layout>
    </MenuContext.Provider>
  );
};

export default AdminLayout;
